import { useState } from 'react';
import { Plus, TriangleAlert } from 'lucide-react';
import { AbschnittRahmen, GesperrtesFeld, ZeilenKopf } from './listenTeile';
import {
  useListenPflege,
  useListenEintragAendern,
  useListenEintragAnlegen
} from '@/hooks/useListenPflege';
import type { ListenEintrag, ListenTyp, ListenZusatz } from '@/hooks/useListenPflege';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { FELD_KLASSE } from '@/lib/feldKlassen';
import { useToast } from '@/lib/toast';
import { ApiError } from '@/lib/api';

/**
 * Eine Konfigurationsliste pflegen (A41).
 *
 * DAS MUSTER STAMMT AUS A36 (MitarbeiterAbschnitt)
 *   Überschrift · ein Satz, woher die Einträge kommen · je Eintrag eine
 *   Zeile, die sich zum Formular aufklappt · gespeichert wird je
 *   Eintrag · Rückmeldung über den Melder · keine Rückfrage davor.
 *
 * EIN BAUTEIL FÜR ALLE LISTEN, NICHT EINES JE LISTE
 *   Es pflegt die Versandarten und die Checklisten-Punkte (A51). Eine
 *   Konfigurationsliste unterscheidet sich von der nächsten in nichts,
 *   was die Oberfläche angeht: Schlüssel, Beschriftung, Reihenfolge,
 *   aktiv. Zwei Komponenten liefen auseinander, sobald jemand nur eine
 *   anfasst (Fallstrick 15). Was sich unterscheidet — Überschrift,
 *   Herkunftssatz, Einzahl — kommt als Eigenschaft herein.
 *
 * DAS ZUSATZFELD (A51)
 *   Genau ein Feld darf eine Liste über code/label/Reihenfolge/aktiv
 *   hinaus haben. Bei den Checklisten-Punkten ist das `scope`: wo der
 *   Haken erscheint. Seine erlaubten Werte und Beschriftungen kommen
 *   vom SERVER (`zusatz` in der Antwort), nicht von hier — sie sind
 *   eine ENUM der Datenbank.
 *
 *   Es steht beim ANLEGEN zur Wahl und danach neben dem Schlüssel unter
 *   demselben Schloss. Ein Punkt, der die Seite wechselt, ließe seine
 *   bereits gesetzten Haken an der falschen Sache hängen.
 *
 * HIER WIRD ANGELEGT, ABER NICHT GELÖSCHT
 *   Anders als bei den Mitarbeitern entstehen diese Einträge nirgendwo
 *   sonst. Gelöscht wird trotzdem nicht: An jedem hängen Vorgänge, und
 *   der Fremdschlüssel stünde danach auf NULL. Der Rückweg heißt
 *   „stilllegen" — der Eintrag verschwindet aus der Auswahl und bleibt
 *   an den alten Vorgängen stehen.
 */
/**
 * Der Wert des Zusatzfeldes eines Eintrags — oder null.
 *
 * Die EINE Stelle, an der der dynamische Zugriff geprüft wird. Der
 * Feldname kommt vom Server, deshalb lässt er sich nicht statisch
 * typisieren; geprüft wird er dafür hier, einmal.
 */
function zusatzWert(eintrag: ListenEintrag, feld: string): string | null {
  const wert = eintrag[feld];
  return typeof wert === 'string' ? wert : null;
}

export function ListenAbschnitt({
  typ,
  titel,
  herkunft,
  einzahl,
  ankerId
}: {
  typ: ListenTyp;
  titel: string;
  /** Ein Satz: woher die Einträge kommen und was hier geht. */
  herkunft: string;
  /** „Versandart" — für Knopf und Meldungen. */
  einzahl: string;
  ankerId: string;
}) {
  const { data, isLoading, isError } = useListenPflege(typ);

  return (
    <AbschnittRahmen
      ankerId={ankerId}
      titel={titel}
      einleitung={herkunft}
      laedt={isLoading}
      fehler={isError}
    >
      {/* `?? []` und nicht `data.eintraege.map`: Antwortet der Server
          ohne das Feld — ältere Fassung, ein Fehlerpfad, ein Prüfstand
          mit anderer Attrappe —, würde die ganze Seite mitgerissen. Ein
          Abschnitt, der leer bleibt, ist ein Ärgernis; eine
          Einstellungsseite, die deshalb weiß bleibt, ist ein Ausfall
          (die Lehre aus A31). */}
      {data !== undefined && (
        <>
          <ul className="space-y-2">
            {(data.eintraege ?? []).map((e) => (
              <li key={e.id}>
                <ListenZeile typ={typ} eintrag={e} zusatz={data.zusatz ?? null} />
              </li>
            ))}
          </ul>
          <NeuerEintrag typ={typ} einzahl={einzahl} zusatz={data.zusatz ?? null} />
        </>
      )}
    </AbschnittRahmen>
  );
}

function ListenZeile({
  typ,
  eintrag,
  zusatz
}: {
  typ: ListenTyp;
  eintrag: ListenEintrag;
  zusatz: ListenZusatz | null;
}) {
  const [offen, setOffen] = useState(false);
  const [label, setLabel] = useState(eintrag.label);
  const [sortierung, setSortierung] = useState(String(eintrag.sort_order));
  const [aktiv, setAktiv] = useState(eintrag.aktiv);
  const aendern = useListenEintragAendern();
  const toast = useToast();

  /** Beim Öffnen den Stand frisch ziehen — ein zweiter Mitarbeiter kann
      inzwischen etwas geändert haben (wie in KundeEditor, B29). */
  function oeffnen(): void {
    setLabel(eintrag.label);
    setSortierung(String(eintrag.sort_order));
    setAktiv(eintrag.aktiv);
    setOffen(true);
  }

  function speichern(): void {
    const zahl = Number.parseInt(sortierung, 10);
    aendern.mutate(
      {
        typ,
        id: eintrag.id,
        label: label.trim(),
        ...(Number.isFinite(zahl) ? { sort_order: zahl } : {}),
        aktiv
      },
      {
        onSuccess: (d) => {
          toast(
            'success',
            d.geaendert.length === 0 ? 'Nichts zu ändern.' : `Gespeichert: ${d.eintrag.label}.`
          );
          setOffen(false);
        },
        onError: (e) => {
          const code = e instanceof ApiError ? e.code : '';
          toast(
            'error',
            code === 'feld_gesperrt'
              ? 'Der Schlüssel lässt sich nicht ändern.'
              : code === 'ungueltig'
                ? 'Bitte die Eingaben prüfen.'
                : 'Speichern fehlgeschlagen.'
          );
        }
      }
    );
  }

  return (
    <div className="rounded-md border border-line bg-cream">
      {/* Gestalt und Kennzeichnung stehen in listenTeile.tsx — dieselbe
          Kopfzeile wie im Wege-Abschnitt. Hier kommt nur herein, worin
          sich die beiden unterscheiden: vorn die Sortiernummer, unten
          der blosse Schlüssel. */}
      <ZeilenKopf
        offen={offen}
        onUmschalten={() => (offen ? setOffen(false) : oeffnen())}
        label={eintrag.label}
        aktiv={eintrag.aktiv}
        vorne={
          <span className="w-8 shrink-0 text-right text-xs tabular-nums text-ink-faint">
            {eintrag.sort_order}
          </span>
        }
        unterzeile={
          <span className="block truncate font-mono text-xs text-ink-soft">{eintrag.code}</span>
        }
      />

      {offen && (
        <div className="space-y-3 border-t border-line px-3 py-3">
          <label className="block text-xs text-ink-soft">
            Beschriftung
            <input
              type="text"
              value={label}
              maxLength={100}
              onChange={(e) => setLabel(e.target.value)}
              className={`mt-1 ${FELD_KLASSE}`}
            />
          </label>

          <label className="block text-xs text-ink-soft">
            Reihenfolge <span className="text-ink-faint">(kleinere Zahl steht oben)</span>
            <input
              type="number"
              // Zahlentastatur auf dem Telefon; ganze Zahlen, kein Komma
              // (A38).
              inputMode="numeric"
              min={0}
              max={9999}
              value={sortierung}
              onChange={(e) => setSortierung(e.target.value)}
              className={`mt-1 ${FELD_KLASSE} tabular-nums`}
            />
          </label>

          <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-md border border-line bg-cream px-3 py-2">
            <input
              type="checkbox"
              checked={aktiv}
              onChange={() => setAktiv((a) => !a)}
              // `accent-akzent`, NICHT `accent-[color:var(--akzent)]`:
              // Die Variable trägt ein RGB-Tripel („29 107 107"), keine
              // Farbe — Tailwind setzt sie erst in `rgb(… / <alpha>)`
              // ein. Die eckige Schreibweise ergibt einen ungültigen
              // Wert, und der Browser malt sein Vorgabe-Blau. Gemessen
              // im gebauten Stand; die beiden Bestandsstellen tragen
              // denselben Fehler (Befund im Bericht).
              className="h-5 w-5 shrink-0 accent-akzent"
            />
            <span className="text-sm text-ink">
              Steht zur Auswahl
              <span className="block text-xs text-ink-soft">
                Abgewählt verschwindet der Eintrag aus den Formularen und bleibt an den
                bisherigen Vorgängen stehen.
              </span>
            </span>
          </label>

          {/* Der Schlüssel steht da, aber gesperrt. */}
          <GesperrtesFeld
            titel="Schlüssel"
            wert={eintrag.code}
            mono
            hilfe={
              <>
                Lässt sich nicht ändern: Der Schlüssel steht im Programm und in den
                Vorgängen, die auf diesen Eintrag zeigen. Gepflegt wird die Beschriftung.
              </>
            }
          />

          {/* Das Zusatzfeld steht UNTER demselben Schloss wie der
              Schlüssel und nicht als Auswahl: Es wird beim Anlegen
              vergeben. Angezeigt wird die Beschriftung des Servers,
              nicht der rohe Wert — „An der Sendung", nicht „sendung". */}
          {zusatz !== null && zusatzWert(eintrag, zusatz.feld) !== null && (
            <GesperrtesFeld
              titel={zusatz.titel}
              wert={
                zusatz.werte[zusatzWert(eintrag, zusatz.feld) ?? ''] ??
                zusatzWert(eintrag, zusatz.feld)
              }
              hilfe={zusatz.hilfe}
            />
          )}

          {eintrag.hinweis !== null && (
            // Warnung, keine Sperre: Stilllegen bleibt erlaubt, aber wer
            // es tut, soll wissen, was daran hängt.
            <p className="flex items-start gap-1.5 rounded-md border border-gold bg-cream-warm px-2 py-1.5 text-xs text-ink">
              <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
              <span>{eintrag.hinweis}</span>
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={speichern} disabled={aendern.isPending}>
              {aendern.isPending ? <Spinner /> : null}
              Speichern
            </Button>
            <Button variant="ghost" onClick={() => setOffen(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Einen Eintrag anlegen.
 *
 * DER SCHLÜSSEL WIRD HIER EINMAL VERGEBEN — und nie wieder. Deshalb
 * steht der Satz darüber schon beim Anlegen und nicht erst hinterher am
 * gesperrten Feld.
 */
function NeuerEintrag({
  typ,
  einzahl,
  zusatz
}: {
  typ: ListenTyp;
  einzahl: string;
  zusatz: ListenZusatz | null;
}) {
  const [offen, setOffen] = useState(false);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  // Die Vorgabe kommt vom Server, nicht aus einer hier notierten
  // Annahme darüber, welcher Wert der übliche ist.
  const [zusatzAuswahl, setZusatzAuswahl] = useState(zusatz?.vorgabe ?? '');
  const anlegen = useListenEintragAnlegen();
  const toast = useToast();

  function abbrechen(): void {
    setCode('');
    setLabel('');
    setZusatzAuswahl(zusatz?.vorgabe ?? '');
    setOffen(false);
  }

  function speichern(): void {
    anlegen.mutate(
      {
        typ,
        code: code.trim(),
        label: label.trim(),
        ...(zusatz !== null ? { zusatz: { feld: zusatz.feld, wert: zusatzAuswahl } } : {})
      },
      {
        onSuccess: (d) => {
          toast('success', `Angelegt: ${d.eintrag.label}.`);
          abbrechen();
        },
        onError: (e) => {
          const fehler = e instanceof ApiError ? e.code : '';
          toast(
            'error',
            fehler === 'code_belegt'
              ? 'Diesen Schlüssel gibt es schon.'
              : fehler === 'ungueltig'
                ? 'Schlüssel: nur Kleinbuchstaben, Ziffern und Unterstrich. Beschriftung darf nicht leer sein.'
                : 'Anlegen fehlgeschlagen.'
          );
        }
      }
    );
  }

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="inline-flex min-h-[44px] items-center gap-2 text-sm text-akzent hover:text-akzent-stark"
      >
        <Plus aria-hidden className="h-4 w-4" />
        {einzahl} hinzufügen
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-line bg-cream-warm p-3">
      <label className="block text-xs text-ink-soft">
        Beschriftung
        <input
          type="text"
          value={label}
          maxLength={100}
          onChange={(e) => setLabel(e.target.value)}
          className={`mt-1 ${FELD_KLASSE}`}
        />
      </label>
      <label className="block text-xs text-ink-soft">
        Schlüssel
        <input
          type="text"
          value={code}
          maxLength={32}
          // Kleinbuchstaben schon beim Tippen: Der Server macht es
          // ohnehin, und ein Feld, das etwas anderes zeigt als
          // hinterher gespeichert wird, ist eine kleine Lüge.
          onChange={(e) => setCode(e.target.value.toLowerCase())}
          placeholder="z. B. hermes"
          className={`mt-1 ${FELD_KLASSE} font-mono`}
        />
        <span className="mt-1 block text-ink-faint">
          Nur Kleinbuchstaben, Ziffern und Unterstrich. Wird einmal vergeben und lässt
          sich danach nicht mehr ändern.
        </span>
      </label>

      {/* Das Zusatzfeld — die einzige Stelle, an der es gesetzt wird.
          Ein <select> und keine Knopfgruppe: drei Werte, einer davon
          fast immer der richtige, und auf dem Tablet ist die
          Systemauswahl das größere Ziel. */}
      {zusatz !== null && (
        <label className="block text-xs text-ink-soft">
          {zusatz.titel}
          <select
            value={zusatzAuswahl}
            onChange={(e) => setZusatzAuswahl(e.target.value)}
            className={`mt-1 min-h-[44px] ${FELD_KLASSE}`}
          >
            {Object.entries(zusatz.werte).map(([wert, beschriftung]) => (
              <option key={wert} value={wert}>
                {beschriftung}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-ink-faint">{zusatz.hilfe}</span>
        </label>
      )}

      {/* A51a — was die getroffene Wahl heute bedeutet.
          Erscheint nur zu den Werten, die eine Warnung tragen: Beim
          Normalfall („An der Bestellung") gibt es nichts zu warnen, und
          ein Hinweis, der immer da steht, wird nicht mehr gelesen.
          Der Satz kommt vom SERVER — er ist eine Aussage über
          detail.php, nicht über dieses Bauteil.
          Grund `gold-soft` statt `cream-warm` wie in der Zeile: Der
          Kasten steht hier IN einem cream-warmen Formular und trüge
          sonst denselben Ton wie sein Untergrund. Gemessen: text-ink
          auf gold-soft = 11,91:1. */}
      {zusatz !== null && (zusatz.warnung?.[zusatzAuswahl] ?? null) !== null && (
        <p className="flex items-start gap-1.5 rounded-md border border-gold bg-gold-soft px-2 py-1.5 text-xs text-ink">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
          <span>{zusatz.warnung?.[zusatzAuswahl]}</span>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          onClick={speichern}
          disabled={anlegen.isPending || code.trim() === '' || label.trim() === ''}
        >
          {anlegen.isPending ? <Spinner /> : null}
          Anlegen
        </Button>
        <Button variant="ghost" onClick={abbrechen}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
